import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Divider,
  Grid,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { getToken } from "firebase/app-check";
import { getAppCheckInstance } from "../../config/ConfigFirebase";
import { PQRSD_STATUS, PQRSD_TYPES } from "../../constants";
import { pqrsdFunctionUrl } from "../../services/backend/PqrsdService";
import CitizenSurveyPanel from "./CitizenSurveyPanel";
import CitizenInfoRequestForm from "./CitizenInfoRequestForm";

import { pqrsdPublicPath, pqrsdText } from "./pqrsdLabel";
const STATUS_LABEL = {
  [PQRSD_STATUS.RADICADO]: { label: "Radicado", color: "info" },
  [PQRSD_STATUS.ASIGNADO]: { label: "Asignado", color: "info" },
  [PQRSD_STATUS.EN_TRAMITE]: { label: "En trámite", color: "warning" },
  [PQRSD_STATUS.RESPONDIDO]: { label: "Respondido", color: "success" },
  [PQRSD_STATUS.CERRADO]: { label: "Cerrado", color: "default" },
  [PQRSD_STATUS.DEVUELTO]: { label: "En reasignación", color: "warning" },
  [PQRSD_STATUS.REABIERTO]: { label: "Reabierta", color: "info" },
  [PQRSD_STATUS.PENDIENTE_INFO]: { label: "Requiere tu información", color: "warning" },
  [PQRSD_STATUS.DESISTIDO]: { label: "Desistida", color: "error" },
  [PQRSD_STATUS.EN_RECURSO]: { label: "Recurso en estudio", color: "warning" },
  [PQRSD_STATUS.ANULADO]: { label: "Anulada", color: "error" },
};

function formatDate(value) {
  if (!value) return "—";
  const d = value instanceof Date ? value : new Date(value);
  if (isNaN(d.getTime())) return "—";
  return new Intl.DateTimeFormat("es-CO", {
    timeZone: "America/Bogota",
    day: "2-digit",
    month: "long",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).format(d);
}

export default function PqrsdTrackPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const trackUrl = useMemo(
    () =>
      process.env.REACT_APP_PQRSD_TRACK_URL ||
      process.env.REACT_APP_PQRSD_PUBLIC_URL ||
      pqrsdFunctionUrl("handlePqrsdPublic"),
    []
  );

  const [radicado, setRadicado] = useState("");
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [magicToken, setMagicToken] = useState(null);
  const [actionBusy, setActionBusy] = useState(false);
  const [actionInfo, setActionInfo] = useState(null);

  const callApi = useCallback(async (body) => {
    if (!trackUrl) throw new Error("La consulta no está configurada en este ambiente.");
    const appCheck = getAppCheckInstance();
    if (!appCheck) throw new Error("App Check no está inicializado");
    // Cached token (renewed by the SDK); forcing a refresh per lookup gets the
    // citizen throttled by App Check.
    const { token } = await getToken(appCheck);
    const resp = await fetch(trackUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-Firebase-AppCheck": token,
      },
      body: JSON.stringify(body),
    });
    if (!resp.ok) {
      if (resp.status === 404) {
        throw new Error(pqrsdText("No encontramos una PQRSD con ese radicado y correo."));
      }
      const json = await resp.json().catch(() => ({}));
      throw new Error(json?.error || "No pudimos completar la operación.");
    }
    return resp.json();
  }, [trackUrl]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setData(null);

    if (!radicado.trim()) {
      setError("Ingresa tu número de radicado.");
      return;
    }
    setLoading(true);
    try {
      const json = await callApi({ radicado: radicado.trim(), email: email.trim() });
      if (!json.result) throw new Error(json.error || "Consulta fallida");
      setData(json);
    } catch (err) {
      setError(err.message || "Error al consultar");
    } finally {
      setLoading(false);
    }
  };

  // Magic-link autoload: ?t=<jwt> -> fetch and auto-populate, no manual entry.
  // Each token is tried once. Retrying on every render re-ran the lookup in a
  // loop for a bad or expired link — the error flashed and vanished, and the
  // function (and App Check) got hit until the tab closed.
  const triedTokenRef = useRef(null);
  useEffect(() => {
    const t = searchParams.get("t");
    if (!t || data || loading || triedTokenRef.current === t) return;
    triedTokenRef.current = t;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const json = await callApi({ token: t });
        if (!json.result) throw new Error(json.error || "Consulta fallida");
        setMagicToken(t);
        setData(json);
      } catch (err) {
        // The function answers in English ("Invalid or expired link"); the
        // citizen needs to know what to do next.
        setError("El enlace no es válido o ya venció. Ingresa tu número de radicado y tu correo para consultar.");
      } finally {
        setLoading(false);
      }
    })();
  }, [searchParams, data, loading, callApi]);

  const refreshData = async () => {
    if (magicToken) {
      const json = await callApi({ token: magicToken }).catch(() => null);
      if (json?.result) setData(json);
    } else if (radicado.trim()) {
      const json = await callApi({ radicado: radicado.trim(), email: email.trim() }).catch(() => null);
      if (json?.result) setData(json);
    }
  };

  // Survey: a rating option closes the PQRSD with that rating.
  const handleRate = async (optionId) => {
    if (!magicToken) return;
    setActionBusy(true);
    setActionInfo(null);
    try {
      const json = await callApi({ token: magicToken, action: "rate", optionId });
      if (!json.result) throw new Error(json.error || "No se pudo enviar la calificación");
      setActionInfo({ severity: "success", text: pqrsdText("Gracias por calificar la respuesta. Su PQRSD se ha cerrado.") });
      await refreshData();
    } finally {
      setActionBusy(false);
    }
  };

  // Survey: the reopen option, with its reason and written detail.
  const handleReopen = async ({ reasonId, detail }) => {
    if (!magicToken) return;
    setActionBusy(true);
    setActionInfo(null);
    try {
      const json = await callApi({ token: magicToken, action: "request_more", reasonId, detail });
      if (!json.result) throw new Error(json.error || "No se pudo reabrir la solicitud");
      setActionInfo({ severity: "info", text: "Recibimos su solicitud. Reanudaremos la gestión y recibirá una nueva respuesta." });
      await refreshData();
    } finally {
      setActionBusy(false);
    }
  };

  const lastResponseEntry = data?.pqrsd?.responses?.slice?.(-1)?.[0] || null;
  // `respondido` is the authoritative "a final answer was filed" signal — the
  // finalizer (pqrsdResponseFlowSync) only sets it for responseType === "final".
  // Gating on it (rather than re-deriving from responses[]) keeps the action
  // visible even on legacy/edge data; the server still enforces the final check.
  const showCitizenActions =
    !!magicToken
    && data?.pqrsd?.status === PQRSD_STATUS.RESPONDIDO
    && !lastResponseEntry?.citizenAction;
  const canRequestMore = data?.pqrsd?.canRequestMore !== false;

  // Email deep-links: ?action=rate (current emails) and the pre-survey
  // ?action=confirm / ?action=more all land on the survey; "more" preselects
  // the reopen option.
  const deepLinkAction = searchParams.get("action");
  const surveyInitialChoice = ["rate", "confirm", "more"].includes(deepLinkAction)
    ? (deepLinkAction === "more" ? "reopen" : "rate")
    : null;

  return (
    <Container maxWidth="md" sx={{ py: 5 }}>
      <Stack spacing={1} alignItems="center" textAlign="center" sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1">
          {pqrsdText("Consultar estado de tu PQRSD")}
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 620 }}>
          Ingresa el número de radicado que recibiste al momento de enviar tu
          solicitud y el correo electrónico que registraste, si registraste uno.
        </Typography>
      </Stack>

      <Paper elevation={1} sx={{ p: 3, mb: 4 }} component="form" onSubmit={onSubmit}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              required
              label="Número de radicado"
              placeholder="PQR_YYYYMMDD-00001"
              value={radicado}
              onChange={(e) => setRadicado(e.target.value)}
              inputProps={{ style: { fontFamily: "monospace" } }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              type="email"
              label="Correo electrónico"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              helperText="Si enviaste tu solicitud de forma anónima sin correo, deja este campo vacío."
            />
          </Grid>
          <Grid item xs={12}>
            <Button
              type="submit"
              variant="contained"
              size="large"
              disabled={loading}
            >
              {loading ? <CircularProgress size={20} /> : <span>Consultar</span>}
            </Button>
          </Grid>
        </Grid>
        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
      </Paper>

      {/* Requerimiento de información (CPACA art. 17). The form lives here, on
          the citizen's own status page: the server mints a scoped reply token
          for whoever already proved they own this PQRSD (magic link, or
          radicado + correo), so the email is the nudge and not the key. */}
      {data?.pqrsd?.pendingInfoRequest && (
        data.pqrsd.pendingInfoRequest.replyToken ? (
          <CitizenInfoRequestForm
            token={data.pqrsd.pendingInfoRequest.replyToken}
            radicado={data.pqrsd.radicado}
            onReplied={(result) => {
              // The form unmounts as soon as the refresh clears the pending
              // requerimiento, so the confirmation has to live out here.
              setActionInfo({
                severity: "success",
                text: result?.deadline
                  ? `¡Gracias! Recibimos tu información y retomamos el trámite. Te responderemos a más tardar el ${formatDate(result.deadline)}.`
                  : "¡Gracias! Recibimos tu información y retomamos el trámite.",
              });
              refreshData();
            }}
          />
        ) : (
          <Alert severity="warning" sx={{ mb: 3 }}>
            La entidad te solicitó información adicional para poder resolver tu solicitud. Abre el
            enlace del correo <strong>&quot;Necesitamos información adicional&quot;</strong> para
            responderla.
          </Alert>
        )
      )}

      {/* Recurso de reposición against a desistimiento (CPACA arts. 74–77): the
          same form, in appeal mode, while the ten business days are running. */}
      {data?.pqrsd?.appealWindow && (
        data.pqrsd.appealWindow.appealToken ? (
          <CitizenInfoRequestForm
            mode="appeal"
            token={data.pqrsd.appealWindow.appealToken}
            radicado={data.pqrsd.radicado}
            onReplied={(result) => {
              setActionInfo({
                severity: "success",
                text: result?.decisionDueAt
                  ? `Recibimos tu recurso de reposición. La entidad lo resolverá a más tardar el ${formatDate(result.decisionDueAt)}.`
                  : "Recibimos tu recurso de reposición.",
              });
              refreshData();
            }}
          />
        ) : (
          <Alert severity="warning" sx={{ mb: 3 }}>
            Tu solicitud fue archivada por desistimiento. Si no estás de acuerdo, puedes interponer un recurso
            de reposición hasta el <strong>{formatDate(data.pqrsd.appealWindow.lastDay)}</strong> desde el
            enlace del correo <strong>&quot;Desistimiento y archivo&quot;</strong>.
          </Alert>
        )
      )}

      {data?.pqrsd?.appeal?.status === "filed" && (
        <Alert severity="info" sx={{ mb: 3 }}>
          Recibimos tu recurso de reposición. La entidad lo está estudiando
          {data.pqrsd.appeal.decisionDueAt
            ? <> y lo resolverá a más tardar el <strong>{formatDate(data.pqrsd.appeal.decisionDueAt)}</strong></>
            : null}
          .
        </Alert>
      )}
      {data?.pqrsd?.appeal?.status === "granted" && (
        <Alert severity="success" sx={{ mb: 3 }}>
          La entidad repuso el desistimiento: tu solicitud volvió a trámite con la información que aportaste.
        </Alert>
      )}
      {data?.pqrsd?.appeal?.status === "denied" && (
        <Alert severity="error" sx={{ mb: 3 }}>
          La entidad confirmó el desistimiento al resolver tu recurso. Puedes radicar una nueva solicitud con la
          información completa cuando quieras.
        </Alert>
      )}
      {data?.pqrsd?.appealFinal && !data?.pqrsd?.appeal && (
        <Alert severity="error" sx={{ mb: 3 }}>
          El plazo para el recurso de reposición venció y el archivo de tu solicitud quedó en firme. Puedes
          radicar una nueva solicitud con la información completa cuando quieras.
        </Alert>
      )}

      {data?.pqrsd && (
        <Paper elevation={1} sx={{ p: 3, mb: 4 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 2 }}>
            <Box>
              <Typography variant="overline" color="text.secondary">
                Radicado
              </Typography>
              <Typography variant="h5" sx={{ fontFamily: "monospace" }}>
                {data.pqrsd.radicado}
              </Typography>
            </Box>
            <Chip
              label={STATUS_LABEL[data.pqrsd.status]?.label || data.pqrsd.status}
              color={STATUS_LABEL[data.pqrsd.status]?.color || "default"}
            />
          </Stack>

          <Divider sx={{ mb: 2 }} />

          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2" color="text.secondary">Tipo</Typography>
              <Typography>{PQRSD_TYPES[data.pqrsd.type] || data.pqrsd.type || "—"}</Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2" color="text.secondary">Fecha de radicación</Typography>
              <Typography>{formatDate(data.pqrsd.createdAt)}</Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2" color="text.secondary">Fecha límite</Typography>
              {/* The stored deadline is frozen while the entity's term is not
                  running, so showing it would tell the citizen a date that no
                  longer means anything. Say why there is no date instead. */}
              {data.pqrsd.status === PQRSD_STATUS.PENDIENTE_INFO ? (
                <Typography>Suspendida mientras completas la información</Typography>
              ) : data.pqrsd.status === PQRSD_STATUS.EN_RECURSO ? (
                <Typography>Suspendida mientras se resuelve tu recurso</Typography>
              ) : data.pqrsd.status === PQRSD_STATUS.DESISTIDO ? (
                <Typography>No aplica: la solicitud fue archivada</Typography>
              ) : data.pqrsd.status === PQRSD_STATUS.ANULADO ? (
                <Typography>No aplica: la solicitud fue anulada</Typography>
              ) : (
                <Typography>
                  {formatDate(data.pqrsd.slaDeadline)}
                  {data.pqrsd.slaBreached && (
                    <Chip label="Vencida" color="error" size="small" sx={{ ml: 1 }} />
                  )}
                </Typography>
              )}
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2" color="text.secondary">Asunto</Typography>
              <Typography>{data.pqrsd.subject || "—"}</Typography>
            </Grid>
          </Grid>

          {data.pqrsd.status === PQRSD_STATUS.ANULADO && (
            <Alert severity="error" variant="outlined" sx={{ mt: 3 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                {pqrsdText("Tu PQRSD fue anulada")}
                {data.pqrsd.annulment?.at ? ` el ${formatDate(data.pqrsd.annulment.at)}` : ""}
              </Typography>
              {data.pqrsd.annulment?.reason && (
                <Typography variant="body2" sx={{ mt: 0.5, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                  <strong>Motivo:</strong> {data.pqrsd.annulment.reason}
                </Typography>
              )}
              <Typography variant="body2" sx={{ mt: 0.5 }}>
                {pqrsdText("No es necesario que realices ningún trámite adicional. Si aún necesitas presentar tu solicitud, puedes radicar una nueva PQRSD.")}
              </Typography>
            </Alert>
          )}

          {data.pqrsd.referenceId && (
            <Box sx={{ mt: 3 }}>
              <Button
                variant="outlined"
                startIcon={<VisibilityIcon />}
                onClick={() => navigate(`/filed/view/${encodeURIComponent(data.pqrsd.referenceId)}`)}
              >
                Ver tu solicitud radicada
              </Button>
            </Box>
          )}

          {actionInfo && (
            <Alert severity={actionInfo.severity} sx={{ mt: 3 }} onClose={() => setActionInfo(null)}>
              {actionInfo.text}
            </Alert>
          )}

          {showCitizenActions && (
            <CitizenSurveyPanel
              survey={data.pqrsd.survey}
              canReopen={canRequestMore}
              maxReopens={data.pqrsd.maxReopens}
              busy={actionBusy}
              initialChoice={surveyInitialChoice}
              onRate={handleRate}
              onReopen={handleReopen}
            />
          )}

          {data.pqrsd.citizenRating?.label && data.pqrsd.status === PQRSD_STATUS.CERRADO && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              Su calificación: <strong>{data.pqrsd.citizenRating.label}</strong>
            </Typography>
          )}

          {Array.isArray(data.answeredRequests) && data.answeredRequests.length > 0 && (
            <Box sx={{ mt: 4 }}>
              <Typography variant="h6" sx={{ mb: 1 }}>
                Información que enviaste
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Lo que respondiste cuando la entidad te pidió completar tu solicitud.
              </Typography>
              {data.answeredRequests.map((req) => (
                <Paper key={req.requestId} variant="outlined" sx={{ p: 2, mb: 1 }}>
                  <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" sx={{ mb: 1 }}>
                    <Chip size="small" color="success" label="Respondido" />
                    <Typography variant="body2" color="text.secondary">
                      Enviaste tu respuesta el {formatDate(req.repliedAt)}
                    </Typography>
                    {req.radicado && (
                      <Typography variant="body2" sx={{ fontFamily: "monospace" }}>
                        · {req.radicado}
                      </Typography>
                    )}
                  </Stack>
                  <Stack spacing={1.5} sx={{ pl: 1, borderLeft: 3, borderColor: "divider" }}>
                    {req.items.map((it) => (
                      <Box key={it.id} sx={{ pl: 1 }}>
                        <Typography variant="subtitle2">{it.label}</Typography>
                        {it.kind === "file" ? (
                          it.file?.url ? (
                            <Button
                              size="small"
                              variant="text"
                              startIcon={<VisibilityIcon />}
                              href={it.file.url}
                              target="_blank"
                              rel="noopener"
                              sx={{ pl: 0 }}
                            >
                              {it.file.name || "Ver el archivo que enviaste"}
                            </Button>
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              No se adjuntó ningún archivo.
                            </Typography>
                          )
                        ) : (
                          <Typography
                            variant="body2"
                            sx={{ whiteSpace: "pre-wrap" }}
                            color={it.answer ? "text.primary" : "text.secondary"}
                          >
                            {it.answer || "Sin responder"}
                          </Typography>
                        )}
                      </Box>
                    ))}
                  </Stack>
                </Paper>
              ))}
            </Box>
          )}

          {Array.isArray(data.communications) && data.communications.length > 0 && (
            <Box sx={{ mt: 4 }}>
              <Typography variant="h6" sx={{ mb: 1 }}>
                Comunicaciones de la entidad
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Documentos que la entidad radicó dentro de tu solicitud.
              </Typography>
              {data.communications.map((c) => (
                <Paper key={c.radicado} variant="outlined" sx={{ p: 2, mb: 1 }}>
                  <Stack
                    direction={{ xs: "column", sm: "row" }}
                    justifyContent="space-between"
                    alignItems={{ xs: "flex-start", sm: "center" }}
                    spacing={1}
                  >
                    <Box>
                      <Typography variant="subtitle2" sx={{ fontFamily: "monospace" }}>
                        {c.radicado}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {c.kindLabel} · {formatDate(c.doneAt || c.createdAt)}
                      </Typography>
                      {c.subject && (
                        <Typography variant="body2" sx={{ mt: 0.5 }}>{c.subject}</Typography>
                      )}
                    </Box>
                    {(c.fileUrl || c.referenceId) && (
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<VisibilityIcon />}
                        href={c.fileUrl || undefined}
                        target={c.fileUrl ? "_blank" : undefined}
                        rel={c.fileUrl ? "noopener" : undefined}
                        onClick={c.fileUrl ? undefined : () => navigate(`/filed/view/${encodeURIComponent(c.referenceId)}`)}
                      >
                        Ver documento
                      </Button>
                    )}
                  </Stack>
                </Paper>
              ))}
            </Box>
          )}

          {Array.isArray(data.responses) && data.responses.length > 0 && (
            <Box sx={{ mt: 4 }}>
              <Typography variant="h6" sx={{ mb: 1 }}>
                Respuestas
              </Typography>
              {data.responses.map((r) => (
                <Paper
                  key={r.radicado}
                  variant="outlined"
                  sx={{
                    p: 2,
                    mb: 1,
                    cursor: r.referenceId ? "pointer" : "default",
                    "&:hover": r.referenceId ? { borderColor: "primary.main", bgcolor: "action.hover" } : {},
                  }}
                  onClick={() => {
                    if (r.referenceId) navigate(`/filed/view/${encodeURIComponent(r.referenceId)}`);
                  }}
                >
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Box>
                      <Typography variant="subtitle2" sx={{ fontFamily: "monospace" }}>
                        {r.radicado}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Respondida · {formatDate(r.doneAt || r.createdAt)}
                      </Typography>
                      {r.subject && (
                        <Typography variant="body2" sx={{ mt: 0.5 }}>{r.subject}</Typography>
                      )}
                    </Box>
                    {r.referenceId && (
                      <Button
                        variant="contained"
                        size="small"
                        startIcon={<VisibilityIcon />}
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/filed/view/${encodeURIComponent(r.referenceId)}`);
                        }}
                      >
                        Ver respuesta
                      </Button>
                    )}
                  </Stack>
                </Paper>
              ))}
            </Box>
          )}
        </Paper>
      )}

      <Box sx={{ textAlign: "center" }}>
        <Button variant="text" onClick={() => navigate(pqrsdPublicPath())}>
          {pqrsdText("Volver a información de PQRSD")}
        </Button>
      </Box>

    </Container>
  );
}
